import { createAsyncThunk } from '@reduxjs/toolkit';

import { log } from '../../../services/logging';
import { updateScene } from '../../../services/scene';
import type { Scene } from '../../../types/scene';
import { isPerspectiveSharingAvailable } from '../../connections/connectionsDuck';
import { isSharedStorageAvailable, sharedStorageApi } from '../../shared-storage/shared-storage.api';
import type { RootState } from '../../types';
import { NAME } from './const';

type UpdateScenePayload = {
  sceneId: Scene['id'];
  update: Scene;
};

type UpdateSceneResult = {
  id: Scene['id'];
  numOfNodes: Scene['numOfNodes'];
  numOfRels: Scene['numOfRels'];
  lastModified: Scene['lastModified'];
};

export const updateSceneThunk = createAsyncThunk<UpdateSceneResult, UpdateScenePayload, { state: RootState }>(
  `${NAME}/updateSceneThunk`,
  async ({ sceneId, update }: UpdateScenePayload, { dispatch, getState }) => {
    const state = getState();
    const isPluginPresent = isPerspectiveSharingAvailable(state);
    let id, numOfNodes, numOfRels, lastModified;

    try {
      if (isPluginPresent) {
        const savedScene = await updateScene(update);
        ({ id, numOfNodes, numOfRels, lastModified } = savedScene ?? {});
      } else if (isSharedStorageAvailable()) {
        const result = await dispatch(
          sharedStorageApi.endpoints.updateScene.initiate({
            sceneId,
            update,
          }),
        );
        ({ id, numOfNodes, numOfRels, lastModified } = result.data ?? {});
      }
    } catch (error) {
      log.error('Failed to update scene:', error);
    }

    if (typeof id !== 'string' || typeof numOfRels !== 'number' || typeof numOfNodes !== 'number' || !lastModified) {
      throw new Error('Could not update the scene.');
    }

    return { id, numOfNodes, numOfRels, lastModified };
  },
);
